import { useId, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { COUNTRY_INDEX } from "@/data/worldCountryIndex";
import { globalPresence } from "@/data/globalPresence";

/** Common alternative names people type. Search only — never used to decide presence. */
const ALIASES = {
  USA: ["usa", "us", "u.s.", "america", "united states of america"],
  GBR: ["uk", "u.k.", "britain", "great britain", "england"],
  MMR: ["burma"],
  ARE: ["uae"],
  VNM: ["viet nam"],
  TUR: ["turkiye"],
  RUS: ["russian federation"],
  COD: ["drc", "dr congo"],
  CIV: ["ivory coast"],
  CZE: ["czech republic"],
  KOR: ["korea"],
};

const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
const ACTIVE = new Set(globalPresence.filter((p) => p.active).map((p) => p.isoCode));
const DISPLAY = new Map(globalPresence.map((p) => [p.isoCode, p.country]));
const ENTRIES = COUNTRY_INDEX.map(([iso, name]) => {
  const label = DISPLAY.get(iso) || name;
  return { iso, label, terms: [norm(label), norm(name), ...(ALIASES[iso] || [])], present: ACTIVE.has(iso) };
});

function search(query) {
  const q = norm(query);
  if (!q) return [];
  const scored = [];
  for (const e of ENTRIES) {
    let best = Infinity;
    for (const t of e.terms) {
      if (t === q) best = Math.min(best, 0);
      else if (t.startsWith(q)) best = Math.min(best, 1);
      else if (t.split(/[\s-]+/).some((w) => w.startsWith(q))) best = Math.min(best, 2);
      else if (q.length > 2 && t.includes(q)) best = Math.min(best, 3);
    }
    if (best < Infinity) scored.push({ e, score: best * 2 + (e.present ? 0 : 1) });
  }
  return scored.sort((a, b) => a.score - b.score || a.e.label.localeCompare(b.e.label)).slice(0, 7).map((s) => s.e);
}

/** "Search country" combobox. Picking a country calls onPick(iso); the map decides how to present it. */
export default function CountrySearch({ onPick, className = "" }) {
  const id = useId();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [empty, setEmpty] = useState(false);
  const results = useMemo(() => search(q), [q]);

  const pick = (entry) => {
    setQ(entry.label);
    setOpen(false);
    setEmpty(false);
    onPick(entry.iso);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (results[active] || results[0]) pick(results[active] || results[0]);
      else if (q.trim()) setEmpty(true);
    } else if (e.key === "Escape") { setOpen(false); }
  };

  const listId = `${id}-list`;
  return (
    <div className={`relative ${className}`} role="search">
      <label htmlFor={`${id}-input`} className="sr-only">Search country</label>
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/45" aria-hidden="true" />
      <input
        id={`${id}-input`}
        type="text"
        value={q}
        autoComplete="off"
        spellCheck={false}
        placeholder="Search country"
        data-testid="gp-search"
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && results[active] ? `${id}-opt-${results[active].iso}` : undefined}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); setEmpty(false); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKeyDown}
        className="h-11 w-full rounded-xl border border-white/15 bg-white/[0.05] pl-10 pr-10 text-[14px] text-white placeholder:text-white/40 transition-colors focus:border-[#3B82F6] focus:bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
      />
      {q && (
        <button type="button" aria-label="Clear search" onClick={() => { setQ(""); setEmpty(false); setOpen(false); }} className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-white/50 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6EA2FF]">
          <X className="h-4 w-4" />
        </button>
      )}
      {open && results.length > 0 && (
        <ul id={listId} role="listbox" aria-label="Matching countries" className="absolute left-0 right-0 top-[calc(100%+6px)] z-40 max-h-72 overflow-auto rounded-xl border border-white/15 bg-[#0B2348]/98 p-1.5 shadow-[0_24px_50px_-18px_rgba(0,0,0,0.8)] backdrop-blur-md">
          {results.map((r, i) => (
            <li
              key={r.iso}
              id={`${id}-opt-${r.iso}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); pick(r); }}
              onMouseEnter={() => setActive(i)}
              className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2 text-[14px] ${i === active ? "bg-white/10 text-white" : "text-white/80"}`}
            >
              <span>{r.label}</span>
              {r.present && <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#7FB0FF]"><span className="h-1.5 w-1.5 rounded-full bg-[#3B82F6]" aria-hidden="true" />Presence</span>}
            </li>
          ))}
        </ul>
      )}
      {empty && <p role="status" className="absolute left-0 top-[calc(100%+6px)] z-40 rounded-lg border border-white/15 bg-[#0B2348]/98 px-3 py-2 text-[13px] text-white/70">No matching country found.</p>}
    </div>
  );
}
